import React from 'react';
import { Users, Plus, Shield, Copy, Check, KeyRound, Trash2, Loader2, RefreshCw, X, Mail, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { team, auth } from '@/lib/api';

const ROLES = ['Principal RMA', 'Case Manager', 'Paralegal', 'Bookkeeper', 'Client Portal'];

const ROLE_TONE = {
  'super_admin': 'bg-red-100 text-red-700 border-red-200',
  'Principal RMA': 'bg-[#2ECC9A]/15 text-[#0F766E] border-[#2ECC9A]/30',
  'principal_rma': 'bg-[#2ECC9A]/15 text-[#0F766E] border-[#2ECC9A]/30',
  'Case Manager': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Paralegal': 'bg-amber-100 text-amber-700 border-amber-200',
  'Bookkeeper': 'bg-slate-100 text-slate-700 border-slate-200',
  'Client Portal': 'bg-sky-100 text-sky-700 border-sky-200',
};

function initials(n) {
  return (n || '?').split(' ').map((s) => s[0] || '').join('').toUpperCase().slice(0, 2);
}

export default function TeamPage() {
  const me = auth.currentUser();
  const canManage = ['super_admin', 'Principal RMA', 'principal_rma'].includes(me?.role);
  const [tab, setTab] = React.useState('team');

  return (
    <div className="p-6" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Team &amp; Clients</h1>
          <div className="text-sm text-slate-500">
            Invite colleagues, adjust roles, and see every applicant your firm is working with.
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="team" data-testid="team-tab-team"><Users className="mr-1.5 h-3.5 w-3.5" /> Team</TabsTrigger>
          <TabsTrigger value="clients" data-testid="team-tab-clients"><Mail className="mr-1.5 h-3.5 w-3.5" /> Clients</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-4">
          <TeamSection canManage={canManage} me={me} />
        </TabsContent>
        <TabsContent value="clients" className="mt-4">
          <ClientsSection canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function TeamSection({ canManage, me }) {
  const [rows, setRows] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [detail, setDetail] = React.useState(null); // last invite result
  const [busyId, setBusyId] = React.useState(null);
  const [msg, setMsg] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await team.members({ q: q || undefined });
      setRows(r.items || []);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load team' });
    }
  }, [q]);

  React.useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Remove this team member? This cannot be undone.')) return;
    setBusyId(id);
    try {
      await team.remove(id);
      setMsg({ ok: true, text: 'Team member removed.' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Remove failed' });
    } finally {
      setBusyId(null);
      setTimeout(() => setMsg(null), 3500);
    }
  };

  const changeRole = async (id, role) => {
    setBusyId(id);
    try { await team.setRole(id, role); load(); }
    catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Role update failed' }); setTimeout(() => setMsg(null), 3500); }
    finally { setBusyId(null); }
  };

  const resetPassword = async (id) => {
    setBusyId(id);
    try {
      const r = await team.resetPassword(id, null, false);
      setDetail({ kind: 'reset', ...r });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Reset failed' });
      setTimeout(() => setMsg(null), 3500);
    } finally { setBusyId(null); }
  };

  if (rows === null) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search by name or email…"
               data-testid="team-search"
               className="h-9 w-full max-w-sm text-xs" />
        <Button onClick={load} variant="outline" className="h-9 gap-1 text-xs" data-testid="team-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
        <div className="flex-1" />
        {canManage && (
          <Button data-testid="team-invite-open" onClick={() => setInviteOpen(true)}
                  className="h-9 gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            <Plus className="h-3.5 w-3.5" /> Invite user
          </Button>
        )}
      </div>

      {msg && (
        <div data-testid="team-msg" className={`mt-3 rounded-md p-2 text-xs ${msg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
          No team members yet. Click "Invite user" to add your first teammate.
        </div>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {rows.map((u) => (
            <Card key={u.id} className="border-slate-200" data-testid={`team-row-${u.id}`}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A3A5C] text-sm font-bold text-white">
                  {initials(u.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <div className="truncate font-semibold text-[#1A3A5C]">{u.name || u.email}</div>
                    <Badge variant="outline" className={`text-[10px] ${ROLE_TONE[u.role] || 'bg-slate-100 text-slate-600'}`}>
                      {u.role}
                    </Badge>
                    {u.id === me?.id && (
                      <Badge variant="outline" className="border-slate-300 text-[10px] text-slate-500">You</Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-slate-500">{u.email} {u.marn ? `· MARN ${u.marn}` : ''}</div>
                </div>
                {canManage && u.id !== me?.id && u.role !== 'super_admin' && (
                  <div className="flex items-center gap-1">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={busyId === u.id}>
                      <SelectTrigger data-testid={`team-role-select-${u.id}`}
                                     className="h-7 w-32 border-slate-200 text-[10px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                            data-testid={`team-reset-${u.id}`}
                            onClick={() => resetPassword(u.id)} disabled={busyId === u.id}
                            title="Reset password">
                      <KeyRound className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                            data-testid={`team-remove-${u.id}`}
                            onClick={() => remove(u.id)} disabled={busyId === u.id}
                            title="Remove">
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)}
                    onInvited={(r) => { setDetail({ kind: 'invite', ...r }); load(); }} />
      <InviteResultDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}


function InviteDialog({ open, onClose, onInvited }) {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [role, setRole] = React.useState('Case Manager');
  const [marn, setMarn] = React.useState('');
  const [sendEmail, setSendEmail] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (open) { setEmail(''); setName(''); setRole('Case Manager'); setMarn(''); setSendEmail(false); setErr(''); }
  }, [open]);

  const submit = async () => {
    if (!email || !name || !role) { setErr('Email, name and role are required'); return; }
    setBusy(true); setErr('');
    try {
      const r = await team.invite({ email: email.trim(), name: name.trim(), role,
                                     marn: marn.trim() || undefined, send_email: sendEmail });
      onInvited?.(r);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Invite failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="team-invite-dialog">
        <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <Label htmlFor="inv-email" className="text-xs">Email</Label>
            <Input id="inv-email" type="email" value={email}
                   data-testid="team-invite-email"
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="colleague@yourfirm.com.au" />
          </div>
          <div>
            <Label htmlFor="inv-name" className="text-xs">Full name</Label>
            <Input id="inv-name" value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="team-invite-name"
                   placeholder="e.g. Priya Nair" />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="inv-marn" className="text-xs">MARN (optional)</Label>
            <Input id="inv-marn" value={marn} onChange={(e) => setMarn(e.target.value)}
                   data-testid="team-invite-marn"
                   placeholder="e.g. 2118541" />
          </div>
          <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
            <Checkbox checked={sendEmail} onCheckedChange={setSendEmail} data-testid="team-invite-send-email" />
            Email login credentials to the invitee (via your configured sender)
          </label>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="team-invite-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="team-invite-submit" onClick={submit} disabled={busy}
                  className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InviteResultDialog({ detail, onClose }) {
  const [copied, setCopied] = React.useState(false);
  if (!detail) return null;
  const isInvite = detail.kind === 'invite';
  const target = detail.user?.email || detail.email;
  const pw = detail.temp_password;

  const copy = async () => {
    try { await navigator.clipboard.writeText(pw); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* noop */ }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="team-invite-result">
        <DialogHeader>
          <DialogTitle>{isInvite ? 'Invite sent — share these credentials' : 'Password reset — share this new password'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
            {isInvite ? (
              <>User <b>{target}</b> can now sign in.</>
            ) : (
              <>Password for user reset. They should sign in with the new password below.</>
            )}
            {detail.email_status?.sent && <div className="mt-1 text-emerald-700">✓ Credentials emailed via your configured sender.</div>}
            {detail.email_status?.requested && !detail.email_status?.sent && (
              <div className="mt-1 text-amber-700">⚠ Email requested but delivery failed — share the password below manually.</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Temporary password</div>
            <div className="mt-1 flex items-center gap-2">
              <code data-testid="team-invite-password" className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-sm">
                {pw}
              </code>
              <Button size="icon" variant="outline" onClick={copy} data-testid="team-invite-copy" className="h-9 w-9">
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="mt-1 text-[10px] text-slate-500">Ask them to change this immediately after first sign-in.</div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="team-invite-result-close" onClick={onClose} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            <Check className="mr-1 h-4 w-4" /> Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ClientsSection() {
  const [rows, setRows] = React.useState(null);
  const [q, setQ] = React.useState('');
  const load = React.useCallback(async () => {
    try { const r = await team.clients({ q: q || undefined }); setRows(r.items || []); }
    catch { setRows([]); }
  }, [q]);
  React.useEffect(() => { load(); }, [load]);

  if (rows === null) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;
  }

  return (
    <div data-testid="clients-section">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search applicant name or email…"
               data-testid="clients-search"
               className="h-9 w-full max-w-sm text-xs" />
        <Button onClick={load} variant="outline" className="h-9 gap-1 text-xs" data-testid="clients-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
        <div className="flex-1" />
        <div className="text-[11px] text-slate-500">
          <b>{rows.length}</b> unique clients across all matters
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
          No applicant contacts on record yet. Once you create matters with applicant emails, they'll appear here.
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Applicant</th>
                <th className="px-3 py-2 font-medium">Country</th>
                <th className="px-3 py-2 font-medium">Matters</th>
                <th className="px-3 py-2 font-medium">Latest</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.email} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`clients-row-${c.email}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#1A3A5C]">{c.name}</div>
                    <div className="text-[10px] text-slate-500">{c.email}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{c.country || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="bg-slate-100 text-slate-700">
                      {c.matter_count} matter{c.matter_count === 1 ? '' : 's'}
                    </Badge>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(c.matters || []).slice(0, 3).map((m) => (
                        <span key={m.id} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[9px] text-slate-600">
                          {m.ref} · {m.subclass}
                        </span>
                      ))}
                      {c.matters?.length > 3 && (
                        <span className="text-[9px] text-slate-500">+{c.matters.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">
                    {c.last_matter_at ? new Date(c.last_matter_at).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] text-[#1A3A5C] hover:bg-slate-50">
                      <ExternalLink className="h-3 w-3" /> Email
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
